"use client";

/**
 * @file        src/components/listing/ListingFilterBar.tsx
 * @description The shared filter bar for both catalogs: a real GET form that applies
 *              filters instantly once hydrated. Renders B3a's controls from a
 *              per-catalog field list.
 * @rule        The form's method/action and the controls' `name` attributes are
 *              LOAD-BEARING for the no-JS and pre-hydration paths (UNIFIED-8) — never
 *              remove them. And never add an always-visible (or auto-hiding) "Применить"
 *              button: the fallback submit belongs inside <noscript> only. Neither the
 *              build nor the linter catches the loss of either.
 * @universal   knowledge/universals/components.md#ListingFilterBar
 */

import { useEffect, useRef, useState, useTransition } from "react";
import { useRouter } from "next/navigation";
import Link from "next/link";
import { SlidersHorizontal, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { buildListingHref, hasActiveFilters, type SearchParamsInput } from "@/lib/listingQuery";
import { FilterRange, FilterSelect, FilterTabs } from "./ListingFilterFields";

// === TYPES ==============================================================

/**
 * One field of a catalog's filter bar. The LIST lives in the page (C2 cars, D1 noscuts)
 * — never here: that is the UNIFIED-5 boundary between shared bar and per-catalog set.
 */
export type ListingFilterField =
  | { kind: "tabs"; name: string; options: readonly { value: string; label: string }[]; allLabel: string }
  | { kind: "select"; name: string; label: string; options: readonly string[] | readonly { value: string; label: string }[]; allLabel: string }
  | {
      kind: "range";
      nameFrom: string;
      nameTo: string;
      label: string;
      placeholderFrom?: string;
      placeholderTo?: string;
      step?: string;
    };

interface ListingFilterBarProps {
  basePath: string;
  searchParams: SearchParamsInput;
  fields: readonly ListingFilterField[];
}

/** Verbatim from the pre-2026-07 client filter (CatalogFilters.tsx, removed at E1) / ListingPager — visual contract, no invented values. */
const RESET = "flex items-center gap-1.5 text-sm text-text-muted transition-colors hover:text-primary";
const MOBILE_BUTTON =
  "mt-4 flex items-center gap-2 rounded-lg border border-border px-4 py-2.5 text-sm font-medium text-text transition-colors hover:bg-surface-alt md:hidden";
const NOSCRIPT_SUBMIT =
  "rounded-xl border border-border px-8 py-3 font-medium text-text transition-colors hover:bg-surface";

const DEBOUNCE_MS = 500;

// === STATE ==============================================================

type Params = Record<string, string>;

/** Flatten the incoming params to a plain record, dropping blanks. */
function toParams(sp: SearchParamsInput): Params {
  const out: Params = {};
  for (const key of Object.keys(sp)) {
    const raw = sp[key];
    const value = Array.isArray(raw) ? raw[0] : raw;
    if (typeof value === "string" && value.trim() !== "") out[key] = value.trim();
  }
  return out;
}

/** Stable serialisation — compared instead of object identity, which changes every render. */
const fingerprint = (p: Params) =>
  Object.keys(p)
    .sort()
    .map((k) => `${k}=${p[k]}`)
    .join("&");

/**
 * The shared filter bar.
 * @input  basePath — the listing route; searchParams — the CURRENT params from the
 *         server page (UNIFIED-1: the URL is the source of truth, not this component);
 *         fields — the per-catalog field list.
 * @output A `<form method="GET">` with the tabs row, the desktop grid and the mobile
 *         Sheet, plus the reset link and the `<noscript>` fallback submit.
 * @important The merged param set is held in a REF and updated synchronously, so two
 *            selections made faster than a server round-trip both survive: the second
 *            push merges into the first instead of into the stale prop. That stale-prop
 *            read is the named defect from the series research — a buyer picking
 *            «Корея» then «Кроссовер» within a second would otherwise lose the country.
 *            Every push goes through `buildListingHref` with an EMPTY patch, which drops
 *            `page` (the required portion reset) and sorts the keys (UNIFIED-7); this
 *            component never manages the page param itself.
 */
export default function ListingFilterBar({ basePath, searchParams, fields }: ListingFilterBarProps) {
  const router = useRouter();
  const [isPending, startTransition] = useTransition();
  const [sheetOpen, setSheetOpen] = useState(false);

  const incoming = toParams(searchParams);
  const incomingPrint = fingerprint(incoming);
  const [params, setParams] = useState<Params>(incoming);
  const [syncedFrom, setSyncedFrom] = useState(incomingPrint);
  const paramsRef = useRef<Params>(incoming);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Re-sync when the server sends different params (React's adjust-state-on-prop-change
  // recipe — compared by fingerprint, since the prop object is new on every render).
  if (incomingPrint !== syncedFrom) {
    setSyncedFrom(incomingPrint);
    setParams(incoming);
  }

  // The ref mirrors the state, written OUTSIDE render (refs must not be touched during
  // it). It exists so two changes in one tick merge instead of racing: an event handler
  // reads the value the previous handler just wrote, not the not-yet-committed state.
  useEffect(() => {
    paramsRef.current = params;
  }, [params]);

  // === HANDLERS =========================================================

  const push = () => {
    if (timer.current) {
      clearTimeout(timer.current);
      timer.current = null;
    }
    const href = buildListingHref(basePath, paramsRef.current, {});
    startTransition(() => router.push(href));
  };

  /** Merge a patch synchronously, then push now or after the debounce window. */
  const apply = (patch: Record<string, string | null>, debounce = false) => {
    const next: Params = { ...paramsRef.current };
    for (const [key, value] of Object.entries(patch)) {
      if (value === null || value.trim() === "") delete next[key];
      else next[key] = value.trim();
    }
    paramsRef.current = next;
    setParams(next);

    if (!debounce) {
      push();
      return;
    }
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      timer.current = null;
      push();
    }, DEBOUNCE_MS);
  };

  // === LAYOUT ===========================================================

  const tabsField = fields.find((f) => f.kind === "tabs");
  const restFields = fields.filter((f) => f.kind !== "tabs");
  const showReset = hasActiveFilters(params);

  /** One field, rendered for a given layout. The suffix keeps desktop and Sheet ids apart. */
  const renderField = (field: ListingFilterField, suffix: string, onChosen?: () => void) => {
    if (field.kind === "range") {
      return (
        <FilterRange
          key={`${field.nameFrom}-${suffix}`}
          idPrefix={`${field.nameFrom}-${suffix}`}
          nameFrom={field.nameFrom}
          nameTo={field.nameTo}
          label={field.label}
          from={params[field.nameFrom] ?? ""}
          to={params[field.nameTo] ?? ""}
          onFromChange={(raw) => apply({ [field.nameFrom]: raw }, true)}
          onToChange={(raw) => apply({ [field.nameTo]: raw }, true)}
          placeholderFrom={field.placeholderFrom}
          placeholderTo={field.placeholderTo}
          step={field.step}
        />
      );
    }
    if (field.kind === "select") {
      return (
        <FilterSelect
          key={`${field.name}-${suffix}`}
          id={`${field.name}-${suffix}`}
          name={field.name}
          label={field.label}
          value={params[field.name] ?? null}
          options={field.options}
          allLabel={field.allLabel}
          onSelect={(value) => {
            apply({ [field.name]: value });
            onChosen?.();
          }}
        />
      );
    }
    return (
      <FilterTabs
        key={`${field.name}-${suffix}`}
        name={field.name}
        value={params[field.name] ?? null}
        options={field.options}
        allLabel={field.allLabel}
        onSelect={(value) => {
          apply({ [field.name]: value });
          onChosen?.();
        }}
      />
    );
  };

  const resetLink = (extra?: string, onClick?: () => void) => (
    <Link href={basePath} onClick={onClick} className={cn(RESET, extra)}>
      <X className="h-3.5 w-3.5" />
      Сбросить фильтры
    </Link>
  );

  return (
    <form
      method="GET"
      action={basePath}
      onSubmit={(e) => {
        e.preventDefault();
        push();
      }}
      aria-busy={isPending}
      data-pending={isPending ? "" : undefined}
      className={cn("transition-opacity", isPending && "opacity-70")}
    >
      {tabsField && renderField(tabsField, "tabs")}

      <div className="mt-4 hidden md:block">
        <div className="grid gap-4 rounded-xl border border-border bg-white p-4 md:grid-cols-2 lg:grid-cols-4">
          {restFields.map((field) => renderField(field, "desktop"))}
        </div>
        {showReset && resetLink("mt-2")}
      </div>

      <button type="button" onClick={() => setSheetOpen(true)} className={MOBILE_BUTTON}>
        <SlidersHorizontal className="h-4 w-4" />
        Фильтры
      </button>

      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetContent side="right" className="w-[85vw] max-w-80 p-6">
          <SheetTitle className="font-heading text-lg font-bold">Фильтры</SheetTitle>
          <div className="mt-6 space-y-4">
            {restFields.map((field) => renderField(field, "sheet", () => setSheetOpen(false)))}
            {showReset && resetLink(undefined, () => setSheetOpen(false))}
          </div>
        </SheetContent>
      </Sheet>

      {/* No-JS fallback ONLY. Never make this an always-visible or auto-hiding button. */}
      <noscript>
        <div className="mt-4">
          <button type="submit" className={NOSCRIPT_SUBMIT}>
            Применить
          </button>
        </div>
      </noscript>
    </form>
  );
}
